import * as Sentry from '@sentry/react';
import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import api, { onUnauthorized, setAuthToken } from 'services/api';
import { trackEvent } from 'utils/tracking';

export interface AuthUser {
  id: string;
  email: string;
  nome: string;
  telefone?: string | null;
  chavePix?: string | null;
  fotoUrl?: string | null;
  authProvider?: string | null;
  createdAt?: string;
  ultimoLogin?: string | null;
  isAdmin: boolean;
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  isAuthenticated: boolean;
  loginWithGoogle: (idToken: string) => Promise<void>;
  loginWithMicrosoft: (idToken: string) => Promise<void>;
  loginWithMagicToken: (token: string) => Promise<void>;
  loginWithOtp: (email: string, code: string) => Promise<void>;
  loginAsGuest: (deviceId: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const STORAGE_TOKEN_KEY = 'lista_auth_token';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const syncSentryUser = useCallback((authUser: AuthUser | null) => {
    try {
      if (!authUser) {
        Sentry.setUser(null);
        return;
      }

      Sentry.setUser({
        id: authUser.id,
        email: authUser.email || undefined,
        username: authUser.nome || undefined,
      });
    } catch (err) {
      // fallback silent
    }
  }, []);

  const clearSession = useCallback(() => {
    setUser(null);
    syncSentryUser(null);
    setAuthToken(null);
    localStorage.removeItem(STORAGE_TOKEN_KEY);
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem('postLoginRedirect');
    }
  }, [syncSentryUser]);

  const logout = useCallback(() => {
    clearSession();
  }, [clearSession]);

  useEffect(() => {
    onUnauthorized(() => {
      clearSession();
    });
  }, [clearSession]);

  const fetchProfile = useCallback(async () => {
    try {
      const response = await api.get('/auth/profile');
      const usuario = response.data.usuario;
      const authUser = usuario ? { ...usuario, isAdmin: !!usuario.isAdmin } : null;
      setUser(authUser);
      syncSentryUser(authUser);
    } catch (error) {
      clearSession();
    } finally {
      setLoading(false);
    }
  }, [clearSession, syncSentryUser]);

  useEffect(() => {
    const storedToken = localStorage.getItem(STORAGE_TOKEN_KEY);
    if (storedToken) {
      setAuthToken(storedToken);
      fetchProfile();
    } else {
      setLoading(false);
    }
  }, [fetchProfile]);

  const loginWithGoogle = useCallback(
    async (idToken: string) => {
      const existingToken = localStorage.getItem(STORAGE_TOKEN_KEY);
      const shouldLinkGuest = user?.authProvider === 'guest' && !!existingToken;
      const response = await api.post('/auth/google', {
        idToken,
        guestToken: shouldLinkGuest ? existingToken : undefined,
      });
      const { token, usuario } = response.data;

      setAuthToken(token);
      localStorage.setItem(STORAGE_TOKEN_KEY, token);
      const authUser = { ...usuario, isAdmin: !!usuario.isAdmin };
      setUser(authUser);
      syncSentryUser(authUser);
      trackEvent('login', {
        provider: 'google',
        isAdmin: !!authUser.isAdmin,
      });
    },
    [syncSentryUser, user?.authProvider]
  );

  const loginWithMicrosoft = useCallback(
    async (idToken: string) => {
      const existingToken = localStorage.getItem(STORAGE_TOKEN_KEY);
      const shouldLinkGuest = user?.authProvider === 'guest' && !!existingToken;
      const response = await api.post('/auth/microsoft', {
        idToken,
        guestToken: shouldLinkGuest ? existingToken : undefined,
      });
      const { token, usuario } = response.data;

      setAuthToken(token);
      localStorage.setItem(STORAGE_TOKEN_KEY, token);
      const authUser = { ...usuario, isAdmin: !!usuario.isAdmin };
      setUser(authUser);
      syncSentryUser(authUser);
      trackEvent('login', {
        provider: 'microsoft',
        isAdmin: !!authUser.isAdmin,
      });
    },
    [syncSentryUser, user?.authProvider]
  );

  const loginWithMagicToken = useCallback(
    async (token: string) => {
      const existingToken = localStorage.getItem(STORAGE_TOKEN_KEY);
      const shouldLinkGuest = user?.authProvider === 'guest' && !!existingToken;
      const response = await api.post('/auth/magic/verify', {
        token,
        guestToken: shouldLinkGuest ? existingToken : undefined,
      });
      const { token: authTokenValue, usuario } = response.data;

      setAuthToken(authTokenValue);
      localStorage.setItem(STORAGE_TOKEN_KEY, authTokenValue);
      const authUser = { ...usuario, isAdmin: !!usuario.isAdmin };
      setUser(authUser);
      syncSentryUser(authUser);
      trackEvent('login', {
        provider: 'magic',
        isAdmin: !!authUser.isAdmin,
      });
    },
    [syncSentryUser, user?.authProvider]
  );

  const loginWithOtp = useCallback(
    async (email: string, code: string) => {
      const existingToken = localStorage.getItem(STORAGE_TOKEN_KEY);
      const shouldLinkGuest = user?.authProvider === 'guest' && !!existingToken;
      const response = await api.post('/auth/otp/verify', {
        email,
        code,
        guestToken: shouldLinkGuest ? existingToken : undefined,
      });
      const { token, usuario } = response.data;

      setAuthToken(token);
      localStorage.setItem(STORAGE_TOKEN_KEY, token);
      const authUser = { ...usuario, isAdmin: !!usuario.isAdmin };
      setUser(authUser);
      syncSentryUser(authUser);
      trackEvent('login', {
        provider: 'otp',
        isAdmin: !!authUser.isAdmin,
      });
    },
    [syncSentryUser, user?.authProvider]
  );

  const loginAsGuest = useCallback(
    async (deviceId: string) => {
      const response = await api.post('/auth/guest', { deviceId });
      const { token, usuario } = response.data;

      setAuthToken(token);
      localStorage.setItem(STORAGE_TOKEN_KEY, token);
      const authUser = { ...usuario, isAdmin: !!usuario.isAdmin };
      setUser(authUser);
      syncSentryUser(authUser);

      trackEvent('login', {
        provider: 'guest',
        isAdmin: !!authUser.isAdmin,
      });
    },
    [syncSentryUser]
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      isAuthenticated: !!user,
      loginWithGoogle,
      loginWithMicrosoft,
      loginWithMagicToken,
      loginWithOtp,
      loginAsGuest,
      logout,
    }),
    [
      user,
      loading,
      loginWithGoogle,
      loginWithMicrosoft,
      loginWithMagicToken,
      loginWithOtp,
      loginAsGuest,
      logout,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};
